import { c as _c } from "react/compiler-runtime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import type { ApiDropVoteEditLog } from "@/generated/models/ApiDropVoteEditLog";
import type { ApiDropVoter } from "@/generated/models/ApiDropVoter";
import type { ApiIdentityOverview } from "@/generated/models/ApiIdentityOverview";
import type { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import { isAutoGeneratedHandle, isEthereumAddress } from "@/helpers/AllowlistToolHelpers";
import { formatNumberWithCommas, getTimeAgoShort } from "@/helpers/Helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
import Image from "next/image";
import Link from "next/link";
interface ParticipationDropVoteDetailsVoterRowProps {
  readonly voter: ApiDropVoter;
  readonly creditType: ApiWaveCreditType;
}
interface ParticipationDropVoteDetailsLogRowProps {
  readonly log: ApiDropVoteEditLog;
  readonly creditType: ApiWaveCreditType;
}
const getIdentityLabel = (identity: ApiIdentityOverview): string => identity.handle ?? identity.primary_address;
const getIdentityTooltipKey = (identity: ApiIdentityOverview): string => identity.handle ?? identity.primary_address;
const shouldLimitIdentityLabel = (label: string): boolean => isEthereumAddress(label) || isAutoGeneratedHandle(label);
const getVoteColorClass = (vote: number): string => {
  if (vote > 0) {
    return "tw-text-emerald-400";
  }
  if (vote < 0) {
    return "tw-text-rose-400";
  }
  return "tw-text-iron-300";
};
const formatSignedVote = (vote: number): string => {
  if (vote === 0) {
    return "0";
  }
  const sign = vote > 0 ? "+" : "-";
  return `${sign}${formatNumberWithCommas(Math.abs(vote))}`;
};
export const normalizeDropVoteLogTimestampMs = (createdAt: number | string | Date): number => {
  if (createdAt instanceof Date) {
    return createdAt.getTime();
  }
  if (typeof createdAt === "string") {
    const asNumber = Number(createdAt);
    if (Number.isFinite(asNumber)) {
      return normalizeDropVoteLogTimestampMs(asNumber);
    }
    return new Date(createdAt).getTime();
  }
  if (!Number.isFinite(createdAt)) {
    return Date.now();
  }
  return createdAt < 1_000_000_000_000 ? createdAt * 1000 : createdAt;
};
function VoterAvatar(t0) {
  const $ = _c(8);
  const {
    identity
  } = t0;
  let t1;
  if ($[0] !== identity) {
    t1 = getIdentityLabel(identity);
    $[0] = identity;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  if (!identity.pfp) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="tw-size-7 tw-flex-shrink-0 tw-rounded-md tw-bg-iron-800 tw-object-contain tw-ring-1 tw-ring-white/10" aria-hidden="true" />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== identity.pfp) {
    t2 = resolveIpfsUrlSync(identity.pfp);
    $[3] = identity.pfp;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `${label}'s avatar`;
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Image unoptimized={true} src={t2} alt={t3} width={28} height={28} className="tw-size-7 tw-flex-shrink-0 tw-rounded-md tw-bg-iron-800 tw-object-contain tw-ring-1 tw-ring-white/10" />;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function VoterIdentityLink(t0) {
  const $ = _c(16);
  const {
    identity
  } = t0;
  let label;
  let t1;
  if ($[0] !== identity) {
    label = getIdentityLabel(identity);
    t1 = shouldLimitIdentityLabel(label);
    $[0] = identity;
    $[1] = label;
    $[2] = t1;
  } else {
    label = $[1];
    t1 = $[2];
  }
  const shouldLimit = t1;
  let t2;
  if ($[3] !== identity) {
    t2 = getIdentityTooltipKey(identity);
    $[3] = identity;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `/${label}`;
  const t4 = `tw-block tw-min-w-0 ${shouldLimit ? "tw-max-w-[8rem] tw-truncate" : ""}`;
  let t5;
  if ($[5] !== label) {
    t5 = <span className="tw-text-sm tw-font-medium tw-text-iron-50 tw-transition-all tw-duration-300 desktop-hover:group-hover:tw-text-iron-300">{label}</span>;
    $[5] = label;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <span className={t4}>{t5}</span>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <Link href={t3} className="tw-group tw-min-w-0 tw-no-underline tw-transition-all tw-duration-300 desktop-hover:hover:tw-opacity-80">{t6}</Link>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t2 || $[14] !== t7) {
    t8 = <UserProfileTooltipWrapper user={t2}>{t7}</UserProfileTooltipWrapper>;
    $[13] = t2;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
export function ParticipationDropVoteDetailsVoterRow(t0) {
  const $ = _c(13);
  const {
    voter,
    creditType
  } = t0;
  let t1;
  if ($[0] !== voter.vote) {
    t1 = getVoteColorClass(voter.vote);
    $[0] = voter.vote;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const voteColorClass = t1;
  let t2;
  if ($[2] !== voter.voter) {
    t2 = <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5"><VoterAvatar identity={voter.voter} /><VoterIdentityLink identity={voter.voter} /></div>;
    $[2] = voter.voter;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `tw-shrink-0 tw-whitespace-nowrap tw-text-sm tw-font-semibold tw-tabular-nums ${voteColorClass}`;
  let t4;
  if ($[4] !== voter.vote) {
    t4 = formatSignedVote(voter.vote);
    $[4] = voter.vote;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = WAVE_VOTING_LABELS[creditType];
  let t6;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== t5) {
    t6 = <span className={t3}>{t4} {t5}</span>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t2 || $[11] !== t6) {
    t7 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-3 tw-py-2.5 tw-transition-colors hover:tw-bg-white/[0.03]">{t2}{t6}</div>;
    $[10] = t2;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
export function ParticipationDropVoteDetailsLogRow(t0) {
  const $ = _c(30);
  const {
    log,
    creditType
  } = t0;
  let t1;
  if ($[0] !== log.new_vote) {
    t1 = getVoteColorClass(log.new_vote);
    $[0] = log.new_vote;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const newVoteColorClass = t1;
  let t2;
  if ($[2] !== log.created_at) {
    t2 = getTimeAgoShort(normalizeDropVoteLogTimestampMs(log.created_at));
    $[2] = log.created_at;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const timeAgo = t2;
  let t3;
  if ($[4] !== log.voter) {
    t3 = <VoterAvatar identity={log.voter} />;
    $[4] = log.voter;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== log.voter) {
    t4 = <VoterIdentityLink identity={log.voter} />;
    $[6] = log.voter;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== timeAgo) {
    t5 = <span className="tw-shrink-0 tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-iron-500">{timeAgo}</span>;
    $[8] = timeAgo;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">{t4}{t5}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== log.old_vote) {
    t7 = log.old_vote === 0 ? <span className="tw-text-iron-500">voted</span> : <><span className="tw-text-iron-500">changed</span><span className="tw-whitespace-nowrap tw-font-medium tw-tabular-nums tw-text-iron-400">{formatSignedVote(log.old_vote)}</span><span className="tw-text-iron-600">{"->"}</span></>;
    $[13] = log.old_vote;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = `tw-whitespace-nowrap tw-font-semibold tw-tabular-nums ${newVoteColorClass}`;
  let t9;
  if ($[15] !== log.new_vote) {
    t9 = formatSignedVote(log.new_vote);
    $[15] = log.new_vote;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = WAVE_VOTING_LABELS[creditType];
  let t11;
  if ($[17] !== t10 || $[18] !== t8 || $[19] !== t9) {
    t11 = <span className={t8}>{t9} {t10}</span>;
    $[17] = t10;
    $[18] = t8;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t7) {
    t12 = <div className="tw-mt-1 tw-flex tw-flex-wrap tw-items-center tw-gap-x-1.5 tw-gap-y-1 tw-text-sm">{t7}{t11}</div>;
    $[21] = t11;
    $[22] = t7;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t6) {
    t13 = <div className="tw-min-w-0 tw-flex-1">{t6}{t12}</div>;
    $[24] = t12;
    $[25] = t6;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t13 || $[28] !== t3) {
    t14 = <div className="tw-px-3 tw-py-2.5 tw-transition-colors hover:tw-bg-white/[0.03]"><div className="tw-flex tw-items-start tw-gap-2.5">{t3}{t13}</div></div>;
    $[27] = t13;
    $[28] = t3;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}